import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Check,
  MapPin,
  MessageSquare,
  PackageX,
  Repeat,
  X,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { Badge, Code } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { ConfirmDialog } from '@/ui/Dialog';
import { Field, Input } from '@/ui/Field';
import { ErrorState, Page, PageBody, PageHeader, Panel, Skeleton } from '@/ui/Panel';
import { useShortcutHint } from '@/app/StatusLine';
import { useAuth } from '@/lib/auth';
import { api, errorMessage } from '@/lib/api/client';
import { money, num, qty } from '@/lib/format';
import type { OrderDetail, OrderItem, ProductSummary } from '@/lib/api/types';
import { Banner, LineStatusBadge } from '@/features/orders/chrome';
import {
  customerLabel,
  fetchOrder,
  isLoggedOnly,
  n,
  ordersKey,
} from '@/features/orders/lib';
import { SearchPicker } from '@/features/orders/SearchPicker';
import { idNum, searchProducts } from '@/features/orders/search';

/* The picking screen.
   One line at a time, in the order the store is walked. The list on the left is
   the route; the card on the right is the only thing you touch. Everything is
   reachable from the keyboard because a picker has a trolley in one hand. */

type Resolution = 'pending' | 'picked' | 'out_of_stock' | 'substituted';

interface Draft {
  status: Resolution;
  /** Picked quantity. Held as text so a half-typed number is not clamped. */
  quantity: string;
  /** Required when the effective product is sold by weight. */
  weight: string;
  substituteId: number | null;
  substituteName: string | null;
  substituteByWeight: boolean;
  note: string;
}

export const Route = createFileRoute('/_app/orders_/$id_/pick')({
  staticData: { crumb: 'Pick' },
  component: PickScreen,
});

function emptyDraft(item: OrderItem): Draft {
  return {
    status: 'pending',
    quantity: String(n(item.orderedQty)),
    weight: '',
    substituteId: null,
    substituteName: null,
    substituteByWeight: false,
    note: '',
  };
}

function PickScreen() {
  const { id } = Route.useParams();
  const orderId = Number(id);
  const navigate = Route.useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();

  const detail = useQuery({
    queryKey: ordersKey.detail(orderId),
    queryFn: () => fetchOrder(orderId),
    enabled: Number.isFinite(orderId) && orderId > 0,
    staleTime: 5_000,
  });

  const order = detail.data;

  // The screen only exists for a claimed order. Anything else belongs on the
  // hub — bounce rather than render a pick UI that cannot submit.
  useEffect(() => {
    if (order && order.status !== 'picking') {
      void navigate({
        to: '/orders/$id',
        params: { id: String(orderId) },
        replace: true,
      });
    }
  }, [order, orderId, navigate]);

  const [drafts, setDrafts] = useState<Record<number, Draft>>({});
  const [cursor, setCursor] = useState(0);
  const [subMode, setSubMode] = useState(false);
  const [abandonOpen, setAbandonOpen] = useState(false);
  const weightRef = useRef<HTMLInputElement>(null);
  const qtyRef = useRef<HTMLInputElement>(null);

  const items = useMemo(() => order?.items ?? [], [order]);

  // Seed once per order load; server-side state wins on a refetch only for
  // lines the operator has not touched in this session.
  useEffect(() => {
    if (items.length === 0) return;
    setDrafts((prev) => {
      const next = { ...prev };
      for (const item of items) {
        if (!next[item.id]) next[item.id] = emptyDraft(item);
      }
      return next;
    });
  }, [items]);

  const draftFor = useCallback(
    (item: OrderItem): Draft => drafts[item.id] ?? emptyDraft(item),
    [drafts]
  );

  const update = useCallback((itemId: number, patch: Partial<Draft>) => {
    setDrafts((prev) => {
      const current = prev[itemId];
      if (!current) return prev;
      return { ...prev, [itemId]: { ...current, ...patch } };
    });
  }, []);

  const current = items[cursor];

  const advance = useCallback(() => {
    setSubMode(false);
    setCursor((c) => {
      // Jump to the next line still pending; fall back to the next in the walk.
      for (let i = c + 1; i < items.length; i += 1) {
        const item = items[i];
        if (item && (drafts[item.id]?.status ?? 'pending') === 'pending') return i;
      }
      return Math.min(c + 1, items.length - 1);
    });
  }, [items, drafts]);

  const markPicked = useCallback(
    (item: OrderItem) => {
      const draft = draftFor(item);
      const byWeight = draft.substituteId
        ? draft.substituteByWeight
        : !!item.soldByWeight;
      if (byWeight && !draft.weight.trim()) {
        weightRef.current?.focus();
        toast.error('This line is sold by weight — enter the actual weight first');
        return;
      }
      update(item.id, { status: draft.substituteId ? 'substituted' : 'picked' });
      advance();
    },
    [draftFor, update, advance]
  );

  const markOut = useCallback(
    (item: OrderItem) => {
      update(item.id, {
        status: 'out_of_stock',
        quantity: '0',
        weight: '',
        substituteId: null,
        substituteName: null,
        substituteByWeight: false,
      });
      advance();
    },
    [update, advance]
  );

  useShortcutHint([
    { keys: 'j / k', label: 'Line' },
    { keys: 'p', label: 'Picked' },
    { keys: 'o', label: 'Out of stock' },
    { keys: 's', label: 'Substitute' },
    { keys: 'w', label: 'Weight' },
  ]);

  // Document-level keys, ignored whenever the operator is typing in a field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing =
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable);
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return;
      const item = items[cursor];
      switch (e.key) {
        case 'j':
        case 'ArrowDown':
          e.preventDefault();
          setSubMode(false);
          setCursor((c) => Math.min(c + 1, items.length - 1));
          break;
        case 'k':
        case 'ArrowUp':
          e.preventDefault();
          setSubMode(false);
          setCursor((c) => Math.max(c - 1, 0));
          break;
        case 'p':
          if (item) {
            e.preventDefault();
            markPicked(item);
          }
          break;
        case 'o':
          if (item) {
            e.preventDefault();
            markOut(item);
          }
          break;
        case 's':
          if (item) {
            e.preventDefault();
            setSubMode(true);
          }
          break;
        case 'w':
          e.preventDefault();
          weightRef.current?.focus();
          break;
        case 'q':
          e.preventDefault();
          qtyRef.current?.focus();
          break;
        case 'Escape':
          // A dialog owns Escape while it is open.
          if (document.querySelector('[role="dialog"]')) return;
          e.preventDefault();
          void navigate({ to: '/orders/$id', params: { id: String(orderId) } });
          break;
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [items, cursor, markPicked, markOut, navigate, orderId]);

  const resolved = items.filter(
    (item) => (drafts[item.id]?.status ?? 'pending') !== 'pending'
  ).length;
  const allResolved = items.length > 0 && resolved === items.length;

  const problems = useMemo(() => {
    const list: string[] = [];
    for (const item of items) {
      const draft = drafts[item.id];
      if (!draft || draft.status === 'pending') continue;
      const quantity = Number(draft.quantity);
      const ordered = n(item.orderedQty);
      if (draft.status === 'out_of_stock') continue;
      if (!Number.isFinite(quantity) || quantity <= 0) {
        list.push(`${item.productNameSnapshot}: picked quantity must be more than zero`);
        continue;
      }
      if (quantity > ordered) {
        list.push(
          `${item.productNameSnapshot}: ${qty(quantity)} picked is more than the ${qty(ordered)} ordered`
        );
      }
      const byWeight = draft.substituteId
        ? draft.substituteByWeight
        : !!item.soldByWeight;
      if (byWeight) {
        const weight = Number(draft.weight);
        if (!draft.weight.trim() || !Number.isFinite(weight) || weight <= 0) {
          list.push(`${item.productNameSnapshot}: needs an actual weight`);
        }
      }
      if (draft.status === 'substituted' && !draft.substituteId) {
        list.push(`${item.productNameSnapshot}: choose the substitute product`);
      }
    }
    return list;
  }, [items, drafts]);

  const submit = useMutation({
    mutationFn: () => {
      const payload = items.map((item) => {
        const draft = draftFor(item);
        const byWeight = draft.substituteId
          ? draft.substituteByWeight
          : !!item.soldByWeight;
        if (draft.status === 'out_of_stock') {
          return {
            orderItemId: item.id,
            pickedQty: 0,
            lineStatus: 'out_of_stock' as const,
            ...(draft.note.trim() ? { pickNote: draft.note.trim() } : {}),
          };
        }
        return {
          orderItemId: item.id,
          pickedQty: Number(draft.quantity),
          lineStatus:
            draft.status === 'substituted'
              ? ('substituted' as const)
              : ('picked' as const),
          ...(byWeight ? { actualWeightLbs: Number(draft.weight) } : {}),
          ...(draft.status === 'substituted' && draft.substituteId
            ? {
                substitutedProductId: draft.substituteId,
                substitutedProductName: draft.substituteName ?? '',
              }
            : {}),
          ...(draft.note.trim() ? { pickNote: draft.note.trim() } : {}),
        };
      });
      return api.post<OrderDetail>(`/orders/${orderId}/pick`, { items: payload });
    },
    onSuccess: (data) => {
      qc.setQueryData(ordersKey.detail(orderId), data);
      void qc.invalidateQueries({ queryKey: ['orders', 'list'] });
      void qc.invalidateQueries({ queryKey: ['orders', 'counts'] });
      void qc.invalidateQueries({ queryKey: ['badges'] });
      toast.success('Pick submitted', {
        description: `${data.orderNumber} totals ${money(data.actualTotalCents)}.`,
      });
      void navigate({ to: '/orders/$id', params: { id: String(orderId) } });
    },
    onError: (e) =>
      toast.error('The pick was not accepted', { description: errorMessage(e) }),
  });

  const abandon = useMutation({
    mutationFn: () => api.post<OrderDetail>(`/orders/${orderId}/abandon`),
    onSuccess: (data) => {
      qc.setQueryData(ordersKey.detail(orderId), data);
      void qc.invalidateQueries({ queryKey: ['orders'] });
      toast.success('Claim released');
      void navigate({ to: '/orders' });
    },
    onError: (e) =>
      toast.error('Could not release the claim', { description: errorMessage(e) }),
  });

  if (detail.isLoading) {
    return (
      <Page>
        <PageHeader eyebrow="Picking" title="Loading the route…" />
        <PageBody>
          <Skeleton className="h-64 w-full" />
        </PageBody>
      </Page>
    );
  }

  if (detail.error || !order) {
    return (
      <Page>
        <PageHeader eyebrow="Picking" title={`Order ${id}`} />
        <PageBody>
          <ErrorState
            title="Could not load this order"
            message={errorMessage(detail.error)}
            onRetry={() => void detail.refetch()}
          />
        </PageBody>
      </Page>
    );
  }

  if (order.status !== 'picking') return null;

  const notMyClaim = order.pickerUserId !== null && order.pickerUserId !== user?.id;
  const pct = items.length === 0 ? 0 : (resolved / items.length) * 100;

  return (
    <Page>
      <PageHeader
        eyebrow="Picking"
        title={
          <span className="flex items-center gap-2.5">
            <span className="font-mono">{order.orderNumber}</span>
            <span className="text-ink-muted text-sm font-normal">
              {customerLabel(order)}
            </span>
          </span>
        }
        subtitle={
          <span className="flex items-center gap-3">
            <span className="font-mono tabular-nums">
              {resolved} / {items.length} resolved
            </span>
            <span className="bg-surface-sunken inline-flex h-1 w-40 overflow-hidden rounded-xs align-middle">
              <span
                className={pct === 100 ? 'bg-positive h-full' : 'bg-signal h-full'}
                style={{ width: `${pct}%` }}
              />
            </span>
          </span>
        }
        actions={
          <>
            <Button
              size="sm"
              variant="ghost"
              icon={<ArrowLeft className="size-3.5" />}
              onClick={() =>
                void navigate({ to: '/orders/$id', params: { id: String(orderId) } })
              }
            >
              Order
            </Button>
            <Button size="sm" variant="danger" onClick={() => setAbandonOpen(true)}>
              Release claim
            </Button>
            {allResolved ? (
              <Button
                size="sm"
                variant="primary"
                icon={<Check className="size-3.5" />}
                disabled={problems.length > 0}
                loading={submit.isPending}
                onClick={() => submit.mutate()}
              >
                Submit pick
              </Button>
            ) : null}
          </>
        }
      />

      <PageBody>
        <div className="space-y-3">
          {notMyClaim ? (
            <Banner tone="warn" title="This pick is claimed by someone else">
              Only the assigned picker can submit these results — a manager or admin can
              override.
            </Banner>
          ) : null}

          {allResolved && problems.length > 0 ? (
            <Banner tone="negative" title="Fix these before submitting">
              <ul className="list-disc pl-4">
                {problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </Banner>
          ) : null}

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[20rem_minmax(0,1fr)]">
            <RouteList
              items={items}
              drafts={drafts}
              cursor={cursor}
              onSelect={(index) => {
                setCursor(index);
                setSubMode(false);
              }}
            />

            {current ? (
              <PickCard
                key={current.id}
                item={current}
                draft={draftFor(current)}
                orderId={orderId}
                index={cursor}
                count={items.length}
                subMode={subMode}
                onSubMode={setSubMode}
                onChange={(patch) => update(current.id, patch)}
                onPicked={() => markPicked(current)}
                onOutOfStock={() => markOut(current)}
                weightRef={weightRef}
                qtyRef={qtyRef}
              />
            ) : null}
          </div>
        </div>
      </PageBody>

      <ConfirmDialog
        open={abandonOpen}
        onOpenChange={setAbandonOpen}
        title="Release this claim?"
        description="The order goes back to the open queue."
        confirmLabel="Release the claim"
        loading={abandon.isPending}
        onConfirm={() => abandon.mutate()}
        consequences={[
          'Everything picked in this session is discarded',
          'All lines reset to pending and reservations rebuild from the ordered quantities',
        ]}
      />
    </Page>
  );
}

/* ── The route ────────────────────────────────────────────────────────────────
   Items arrive pre-sorted into walking order (zone sort → pick sequence). We
   group consecutive lines by zone and never re-sort: the sequence IS the path
   through the store.                                                         */

function RouteList({
  items,
  drafts,
  cursor,
  onSelect,
}: {
  items: OrderItem[];
  drafts: Record<number, Draft>;
  cursor: number;
  onSelect: (index: number) => void;
}) {
  const groups: Array<{ zone: string; rows: Array<{ item: OrderItem; index: number }> }> =
    [];
  items.forEach((item, index) => {
    const zone = item.zoneName ?? 'Unzoned';
    const last = groups[groups.length - 1];
    if (last && last.zone === zone) last.rows.push({ item, index });
    else groups.push({ zone, rows: [{ item, index }] });
  });

  return (
    <Panel
      title="Pick route"
      description={`${groups.length} zones`}
      bleed
      className="self-start"
    >
      <div>
        {groups.map((group) => (
          <div key={`${group.zone}-${group.rows[0]?.index ?? 0}`}>
            <div className="border-line-soft bg-surface-sunken text-2xs text-ink-faint flex items-center gap-1.5 border-y px-3 py-1 tracking-wider uppercase">
              <MapPin className="size-3" />
              {group.zone}
              <span className="ml-auto font-mono">{num(group.rows.length)}</span>
            </div>
            {group.rows.map(({ item, index }) => {
              const status = drafts[item.id]?.status ?? 'pending';
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onSelect(index)}
                  className={cn(
                    'border-line-soft flex w-full items-center gap-2 border-b px-3 py-1.5 text-left',
                    index === cursor ? 'bg-signal-muted' : 'hover:bg-surface-hover'
                  )}
                >
                  <span
                    className={cn(
                      'size-1.5 shrink-0 rounded-full',
                      status === 'pending'
                        ? 'bg-line-strong'
                        : status === 'out_of_stock'
                          ? 'bg-negative'
                          : status === 'substituted'
                            ? 'bg-info'
                            : 'bg-positive'
                    )}
                    aria-hidden
                  />
                  <span className="text-ink min-w-0 flex-1 truncate text-xs">
                    {item.productNameSnapshot}
                  </span>
                  <span className="text-2xs text-ink-faint shrink-0 font-mono tabular-nums">
                    {qty(item.orderedQty)}
                  </span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </Panel>
  );
}

/* ── The one line you are working ─────────────────────────────────────────── */

function PickCard({
  item,
  draft,
  orderId,
  index,
  count,
  subMode,
  onSubMode,
  onChange,
  onPicked,
  onOutOfStock,
  weightRef,
  qtyRef,
}: {
  item: OrderItem;
  draft: Draft;
  orderId: number;
  index: number;
  count: number;
  subMode: boolean;
  onSubMode: (on: boolean) => void;
  onChange: (patch: Partial<Draft>) => void;
  onPicked: () => void;
  onOutOfStock: () => void;
  weightRef: React.RefObject<HTMLInputElement | null>;
  qtyRef: React.RefObject<HTMLInputElement | null>;
}) {
  const qc = useQueryClient();
  const byWeight = draft.substituteId ? draft.substituteByWeight : !!item.soldByWeight;
  const ordered = n(item.orderedQty);

  // A weight line cannot be resolved without a number; put the cursor there.
  useEffect(() => {
    if (byWeight) weightRef.current?.focus();
  }, [byWeight, item.id, weightRef]);

  const ask = useMutation({
    mutationFn: (substitute: { id: number; name: string }) =>
      api.post<{ notificationId: number; channel: string }>(
        `/orders/${orderId}/notifications/substitution-request`,
        {
          orderItemId: item.id,
          substituteProductId: substitute.id,
          substituteProductName: substitute.name,
        }
      ),
    onSuccess: async (result) => {
      await qc.invalidateQueries({ queryKey: ordersKey.detail(orderId) });
      // The request is only logged unless a message provider is connected —
      // never say the customer was asked when they were not (TASK-0226).
      if (isLoggedOnly(result)) {
        toast.warning('Substitution question logged only', {
          description:
            'The customer was not messaged — ask them yourself, then record their answer on the order.',
        });
      } else {
        toast.success('Asked the customer', {
          description: 'The order shows an awaiting-response banner until they answer.',
        });
      }
    },
    onError: (e) =>
      toast.error('Could not send the request', { description: errorMessage(e) }),
  });

  return (
    <Panel
      title={`Line ${index + 1} of ${count}`}
      actions={
        <LineStatusBadge status={draft.status === 'pending' ? null : draft.status} />
      }
    >
      <div className="space-y-4">
        <div>
          <div className="flex items-baseline gap-2">
            <h3 className="text-ink min-w-0 text-lg font-medium">
              {item.productNameSnapshot}
            </h3>
            {item.soldByWeight ? <Badge tone="info">By weight</Badge> : null}
          </div>
          <div className="text-2xs text-ink-faint mt-1 flex flex-wrap items-center gap-2">
            {item.zoneName ? (
              <span className="flex items-center gap-1">
                <MapPin className="size-3" />
                {item.zoneName}
              </span>
            ) : null}
            {item.pickSequence !== null && item.pickSequence !== undefined ? (
              <Code>seq {item.pickSequence}</Code>
            ) : null}
            <span className="font-mono">
              {qty(item.orderedQty)} ordered · {money(item.estimatedUnitPriceCents)} est.
            </span>
            {item.substitutionPref ? <span>Pref: {item.substitutionPref}</span> : null}
          </div>
        </div>

        {draft.substituteName ? (
          <div className="border-info/30 bg-info-muted flex items-center gap-2 rounded border px-2.5 py-1.5 text-xs">
            <Repeat className="text-info size-3.5 shrink-0" />
            <span className="text-ink min-w-0 flex-1 truncate">
              Substituting with {draft.substituteName}
            </span>
            <Button
              size="icon-sm"
              variant="ghost"
              title="Drop the substitution"
              onClick={() =>
                onChange({
                  substituteId: null,
                  substituteName: null,
                  substituteByWeight: false,
                  status: draft.status === 'substituted' ? 'pending' : draft.status,
                })
              }
            >
              <X className="size-3.5" />
            </Button>
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Picked quantity"
            hint={`Cannot exceed the ${qty(ordered)} ordered.`}
          >
            <Input
              ref={qtyRef}
              mono
              inputMode="decimal"
              value={draft.quantity}
              invalid={Number(draft.quantity) > ordered}
              onChange={(e) => onChange({ quantity: e.currentTarget.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onPicked();
                }
                // Escape hands the keyboard back to p / o / s.
                if (e.key === 'Escape') e.currentTarget.blur();
              }}
            />
          </Field>

          {byWeight ? (
            <Field
              label="Actual weight (lb)"
              required
              hint="The server prices this line from the scale weight. Enter commits; Esc frees the shortcut keys."
            >
              <Input
                ref={weightRef}
                mono
                inputMode="decimal"
                value={draft.weight}
                placeholder="0.000"
                invalid={draft.weight.trim() !== '' && !(Number(draft.weight) > 0)}
                onChange={(e) => onChange({ weight: e.currentTarget.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    onPicked();
                  }
                  if (e.key === 'Escape') e.currentTarget.blur();
                }}
              />
            </Field>
          ) : null}
        </div>

        <Field label="Pick note" hint="Optional. Travels with the line.">
          <Input
            value={draft.note}
            placeholder="Last two on the shelf…"
            onChange={(e) => onChange({ note: e.currentTarget.value })}
          />
        </Field>

        {subMode ? (
          <Field
            label="Substitute with"
            hint="Choosing a product marks this line substituted. Weight rules follow the substitute."
            action={
              <button
                type="button"
                className="text-2xs text-ink-faint hover:text-ink"
                onClick={() => onSubMode(false)}
              >
                Cancel
              </button>
            }
          >
            <SearchPicker<ProductSummary>
              autoFocus
              queryKey={['products', 'search']}
              search={(term, signal) => searchProducts(term, signal)}
              itemKey={(p) => p.id}
              onCancel={() => onSubMode(false)}
              onSelect={(product) => {
                onChange({
                  substituteId: idNum(product.id),
                  substituteName: product.name,
                  substituteByWeight: !!product.soldByWeight,
                  status: 'substituted',
                  weight: '',
                });
                onSubMode(false);
              }}
              placeholder="Search the catalogue…"
              renderItem={(p) => (
                <>
                  <span className="min-w-0 flex-1 truncate">{p.name}</span>
                  {p.soldByWeight ? <span className="text-2xs shrink-0">/lb</span> : null}
                  <span className="text-2xs shrink-0 font-mono tabular-nums">
                    {money(p.price)}
                  </span>
                </>
              )}
            />
            {draft.substituteId ? (
              <Button
                size="xs"
                className="mt-1.5"
                icon={<MessageSquare className="size-3" />}
                loading={ask.isPending}
                onClick={() =>
                  ask.mutate({
                    id: draft.substituteId!,
                    name: draft.substituteName ?? '',
                  })
                }
              >
                Ask the customer first
              </Button>
            ) : null}
          </Field>
        ) : null}

        <div className="border-line-soft flex flex-wrap items-center gap-2 border-t pt-3">
          <Button
            variant="primary"
            icon={<Check className="size-3.5" />}
            kbd="p"
            onClick={onPicked}
          >
            {draft.substituteId ? 'Substituted' : 'Picked'}
          </Button>
          <Button
            variant="danger"
            icon={<PackageX className="size-3.5" />}
            kbd="o"
            onClick={onOutOfStock}
          >
            Out of stock
          </Button>
          {!subMode ? (
            <Button
              icon={<Repeat className="size-3.5" />}
              kbd="s"
              onClick={() => onSubMode(true)}
            >
              Substitute
            </Button>
          ) : null}
        </div>
      </div>
    </Panel>
  );
}
